/**
 * SessionDetailPage — the Classcard-parity session page (11E.7).
 *
 * Layout: header strip (date / weekday / time range / timezone, colour-keyed
 * to the staff member) + Actions menu, a left rail of session facts, and a
 * Register as the main body.
 *
 * Cross-feature composition
 * -------------------------
 * The Register needs the attendance roster (Phase 5) and booking writes
 * (Phase 6), and `src/features/calendar/**` may not import either sibling.
 * So the Register is injected as a render prop from the composition root
 * (`src/routes/index.tsx`), exactly like the Bookings page's pickers.
 * This page owns the session, the class, the actions and the chrome; it
 * knows nothing about how enrolment is stored.
 *
 * Enrolment is a booking
 * ----------------------
 * There is no separate "enrolment" record. `bookings` is the sole producer
 * of `session.student_ids` (it maintains the array atomically on create and
 * cancel), and Phase 5 reads that array as the roster. Writing enrolment
 * any other way would create the second source of truth the 11E.7
 * cross-reflection invariant forbids.
 */

import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, Modal } from '@/components/common';
import { PageContainer } from '@/components/layout';
import { useAuth } from '@/context';
import { ApiError } from '@core/api/client';
import { useClass } from '@features/calendar/api/classes';
import {
  useCancelSession,
  useDeleteSession,
  useSession,
  type SessionResponse,
} from '@features/calendar/api/sessions';
import { useSubjectOptions, useTeacherOptions } from '@features/calendar/api/lookups';
import { EditRepeatsModal } from '@features/calendar/components/EditRepeatsModal';
import { staffColor } from '@features/calendar/utils/staffColor';
import { fmtTimeInTz, inTenantTz } from '@features/calendar/utils/tz';

export interface SessionDetailPageProps {
  /**
   * Renders the Register (roster + enrol/unenrol + mark attendance).
   * Injected at the composition root so this page stays inside the
   * calendar feature's import boundary.
   */
  renderRegister?: (session: SessionResponse) => React.ReactNode;
  /** Grades link target — Phase 10B surfacing lands in 11E.8. */
  renderGradesLink?: (session: SessionResponse) => React.ReactNode;
}

const RAIL_LABEL: React.CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: '#64748b',
  fontWeight: 600,
};

const RAIL_VALUE: React.CSSProperties = { fontSize: 14, color: '#0f172a' };

function durationMinutes(startIso: string, endIso: string): number {
  return Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000);
}

export const SessionDetailPage: React.FC<SessionDetailPageProps> = ({
  renderRegister,
  renderGradesLink,
}) => {
  const { id: sessionId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantTz = user?.tenant_timezone ?? 'UTC';

  const { data: session, isLoading, isError, refetch } = useSession(sessionId);
  const { data: klass } = useClass(session?.class_id);
  const { data: subjectOptions } = useSubjectOptions();
  const { data: teacherOptions } = useTeacherOptions();

  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsBtnRef = useRef<HTMLSpanElement>(null);
  const [actionsMenuPos, setActionsMenuPos] = useState<{ top: number; right: number } | null>(
    null,
  );
  const [repeatsOpen, setRepeatsOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);

  const cancelMut = useCancelSession();
  const deleteMut = useDeleteSession();

  const subjectName = useMemo(
    () => subjectOptions?.find((o) => o.value === session?.subject_id)?.label,
    [subjectOptions, session?.subject_id],
  );
  const teacherName = useMemo(
    () => teacherOptions?.find((o) => o.value === session?.teacher_id)?.label,
    [teacherOptions, session?.teacher_id],
  );

  if (isLoading) {
    return (
      <PageContainer title="Session">
        <Card padding="lg">
          <div data-testid="candeza-session-loading" style={{ color: '#64748b' }}>
            Loading session…
          </div>
        </Card>
      </PageContainer>
    );
  }

  if (isError || !session) {
    return (
      <PageContainer title="Session">
        <Card padding="lg">
          <div data-testid="candeza-session-error" style={{ color: '#dc2626' }}>
            Failed to load this session.{' '}
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </div>
        </Card>
      </PageContainer>
    );
  }

  const startTz = inTenantTz(new Date(session.starts_at), tenantTz);
  const colors = staffColor(session.teacher_id);
  const attendanceMarked = Boolean(session.attendance_marked_at);
  const isCancelled = session.status === 'cancelled';

  const submitCancel = async () => {
    if (!cancelReason.trim()) return;
    try {
      await cancelMut.mutateAsync({ id: session.id, body: { reason: cancelReason.trim() } });
      // The backend queues the Phase 9 class-cancelled email to every
      // enrolled student's guardians as a background task.
      toast.success('Class cancelled — guardians notified');
      setCancelOpen(false);
      setCancelReason('');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Cancel failed');
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteMut.mutateAsync(session.id);
      toast.success('Session deleted');
      navigate('/calendar');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Delete failed');
      setDeleteOpen(false);
    }
  };

  return (
    <PageContainer
      title={subjectName ?? 'Session'}
      subtitle={teacherName ? `with ${teacherName}` : undefined}
      actions={
        <div>
          {/* `Button` is a plain function component (no forwardRef), so the
              measurement ref sits on this wrapper span instead — it hugs
              the button tightly (inline-block), so its rect is the
              button's rect. */}
          <span ref={actionsBtnRef} style={{ display: 'inline-block' }}>
            <Button
              data-testid="candeza-session-actions-btn"
              aria-haspopup="menu"
              aria-expanded={actionsOpen}
              onClick={() => {
                // Portalled to <body> (see below) because this button lives
                // inside PageContainer's `actions` slot, which react-
                // awesome-reveal's <Fade> wraps in an overflow:hidden div —
                // an in-place `position: absolute` menu here gets clipped to
                // invisibility the instant it overflows that box. Position
                // is computed from the trigger's own rect since a portalled
                // element can no longer rely on CSS `position: absolute`
                // relative to its logical parent.
                if (!actionsOpen && actionsBtnRef.current) {
                  const rect = actionsBtnRef.current.getBoundingClientRect();
                  setActionsMenuPos({
                    top: rect.bottom + 4,
                    right: window.innerWidth - rect.right,
                  });
                }
                setActionsOpen((o) => !o);
              }}
            >
              Actions ▾
            </Button>
          </span>
          {actionsOpen &&
            actionsMenuPos &&
            createPortal(
              <div
                role="menu"
                data-testid="candeza-session-actions-menu"
                style={{
                  position: 'fixed',
                  top: actionsMenuPos.top,
                  right: actionsMenuPos.right,
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(15,23,42,.12)',
                  minWidth: 210,
                  zIndex: 1000,
                  overflow: 'hidden',
                }}
              >
                <MenuItem
                  testId="candeza-session-action-edit"
                  onClick={() => {
                    setActionsOpen(false);
                    navigate(`/calendar?session=${session.id}`);
                  }}
                >
                  Edit
                </MenuItem>
                <MenuItem
                  testId="candeza-session-action-edit-repeats"
                  onClick={() => {
                    setActionsOpen(false);
                    setRepeatsOpen(true);
                  }}
                >
                  Edit repeats…
                </MenuItem>
                <MenuItem
                  testId="candeza-session-action-duplicate"
                  onClick={() => {
                    setActionsOpen(false);
                    setRepeatsOpen(false);
                    toast('Pick a new slot on the calendar to duplicate into.');
                    navigate(`/calendar?duplicate=${session.id}`);
                  }}
                >
                  Duplicate
                </MenuItem>
                <MenuItem
                  testId="candeza-session-action-cancel"
                  disabled={isCancelled}
                  onClick={() => {
                    setActionsOpen(false);
                    setCancelOpen(true);
                  }}
                >
                  Cancel this class
                </MenuItem>
                <MenuItem
                  testId="candeza-session-action-delete"
                  danger
                  disabled={attendanceMarked}
                  title={
                    attendanceMarked
                      ? 'Attendance has been marked — cancel this class instead of deleting it.'
                      : undefined
                  }
                  onClick={() => {
                    setActionsOpen(false);
                    setDeleteOpen(true);
                  }}
                >
                  Delete
                </MenuItem>
              </div>,
              document.body,
            )}
        </div>
      }
    >
      {/* Header strip — colour-keyed to the staff member, same hash the
          calendar chips use, so a session reads as "the same colour" in
          both places. */}
      <Card padding="lg" data-testid="candeza-session-detail-page">
        <div
          data-testid="candeza-session-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            borderLeft: `6px solid ${colors.accent}`,
            background: colors.bg,
            color: colors.fg,
            borderRadius: 8,
            padding: '14px 18px',
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {startTz.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
          </div>
          <div style={{ fontSize: 14, opacity: 0.85 }}>
            {startTz.toLocaleDateString('en-GB', { weekday: 'long' })}
          </div>
          <div
            data-testid="candeza-session-timerange"
            style={{ fontSize: 15, fontWeight: 600 }}
          >
            {fmtTimeInTz(session.starts_at, tenantTz)}–{fmtTimeInTz(session.ends_at, tenantTz)}
          </div>
          <div
            data-testid="candeza-session-timezone"
            style={{ fontSize: 12, opacity: 0.75 }}
          >
            {tenantTz}
          </div>
          {isCancelled && (
            <span
              data-testid="candeza-session-cancelled-badge"
              style={{
                marginLeft: 'auto',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: 999,
                padding: '3px 10px',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              Cancelled
            </span>
          )}
          {attendanceMarked && !isCancelled && (
            <span
              data-testid="candeza-session-attendance-badge"
              style={{
                marginLeft: 'auto',
                background: '#dcfce7',
                color: '#166534',
                borderRadius: 999,
                padding: '3px 10px',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              ✓ Attendance marked
            </span>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(200px, 240px) 1fr',
            gap: 24,
            alignItems: 'start',
          }}
        >
          {/* Left rail */}
          <aside
            data-testid="candeza-session-rail"
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <div>
              <div style={RAIL_LABEL}>Subject</div>
              <div style={RAIL_VALUE}>
                {subjectName ?? session.subject_id}{' '}
                <span style={{ color: '#64748b', fontSize: 12 }}>
                  ({durationMinutes(session.starts_at, session.ends_at)} min)
                </span>
              </div>
            </div>
            <div>
              <div style={RAIL_LABEL}>Staff</div>
              <div style={RAIL_VALUE}>{teacherName ?? session.teacher_id}</div>
            </div>
            <div>
              <div style={RAIL_LABEL}>Location</div>
              <div style={RAIL_VALUE}>
                {session.room_id ?? session.branch_id ?? '—'}
              </div>
            </div>
            <div>
              <div style={RAIL_LABEL}>Capacity</div>
              <div style={RAIL_VALUE} data-testid="candeza-session-capacity">
                {session.booked_count ?? (session.student_ids?.length ?? 0)}
                {session.capacity != null ? ` / ${session.capacity}` : ''}
              </div>
            </div>
            <div>
              <div style={RAIL_LABEL}>Reminders</div>
              <div style={{ ...RAIL_VALUE, color: '#64748b', fontSize: 13 }}>
                Guardians are emailed automatically when this class is cancelled.
              </div>
            </div>
            <div>
              <div style={RAIL_LABEL}>Grades</div>
              <div style={RAIL_VALUE}>
                {renderGradesLink ? (
                  renderGradesLink(session)
                ) : (
                  // Phase 10B exists; surfacing it here is 11E.8's job (D5).
                  <span style={{ color: '#94a3b8', fontSize: 13 }}>
                    Progress reports — coming in 11E.8
                  </span>
                )}
              </div>
            </div>
          </aside>

          {/* Register */}
          <section data-testid="candeza-session-register">
            <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Register</h3>
            {renderRegister ? (
              renderRegister(session)
            ) : (
              <div style={{ color: '#64748b', fontSize: 14 }}>
                Register unavailable — attendance module not wired.
              </div>
            )}
          </section>
        </div>
      </Card>

      {repeatsOpen && klass && (
        <EditRepeatsModal
          isOpen
          sessionId={session.id}
          currentClass={klass}
          onClose={() => setRepeatsOpen(false)}
        />
      )}

      {cancelOpen && (
        <Modal
          isOpen
          onClose={() => setCancelOpen(false)}
          title="Cancel this class"
          data-testid="candeza-session-cancel-modal"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>
              Reason
              <textarea
                data-testid="candeza-session-cancel-reason-input"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: 8 }}
              />
            </label>
            <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
              Every enrolled student's guardians will be emailed automatically.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" onClick={() => setCancelOpen(false)}>
                Back
              </Button>
              <Button
                data-testid="candeza-session-cancel-submit-btn"
                onClick={() => void submitCancel()}
                disabled={cancelMut.isPending || !cancelReason.trim()}
              >
                {cancelMut.isPending ? 'Cancelling…' : 'Cancel class'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {deleteOpen && (
        <Modal
          isOpen
          onClose={() => setDeleteOpen(false)}
          title="Delete this session?"
          data-testid="candeza-session-delete-modal"
          size="sm"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 14, margin: 0 }}>
              This cannot be undone. Use Cancel instead if you want a record kept.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Button variant="outline" onClick={() => setDeleteOpen(false)}>
                Back
              </Button>
              <Button
                data-testid="candeza-session-delete-confirm-btn"
                onClick={() => void confirmDelete()}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
};

// ---------------------------------------------------------------------------

interface MenuItemProps {
  testId: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
  title?: string;
}

const MenuItem: React.FC<MenuItemProps> = ({
  testId,
  onClick,
  children,
  disabled,
  danger,
  title,
}) => (
  <button
    type="button"
    role="menuitem"
    data-testid={testId}
    disabled={disabled}
    title={title}
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      textAlign: 'left',
      padding: '9px 14px',
      border: 'none',
      background: 'transparent',
      fontSize: 14,
      cursor: disabled ? 'not-allowed' : 'pointer',
      color: disabled ? '#cbd5e1' : danger ? '#dc2626' : '#0f172a',
    }}
  >
    {children}
  </button>
);
